import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {PLANS} from '../../../upgrade_to_dashbo/pricingConfig';
import {ArrowLeft, Check} from '../../../icons';
import {
    CURRENCY_IDS,
    NS,
    PERIOD_IDS,
    addonPriceText,
    baseTier,
    currentQuantitiesOf,
    defaultChooserState,
    formatAmount,
    formatLongDate,
    listMonthly,
    nextPlanOf,
    periodDiscount,
    planDelta,
    planDirection,
    planFit,
    retentionAmountText,
    showsRoomBadge,
} from './subscriptionPageModel';
import {LinkButton} from './SubscriptionParts';

const RAW = {escapeValue: false};

/**
 * «Cambiar de plan» (mockup 2), en la misma pantalla con `#plan-change`. Cada plan dice cómo le queda a ESTA
 * agencia (`plans.fit_*`) y, si se mira en la moneda y el período de lo que paga, cuánto más o menos sale.
 * Subir o bajar abre la confirmación (el padre); de ahí el pedido va al chat (D-2): no hay cobro en la app.
 * Los adicionales siguen la moneda elegida, que abre en la de la agencia: en pesos, ARS más IVA.
 */
export function PlanChooser({tier, isCustom, price, subscriptionCurrency, usage, onBack, onPickPlan,
                                onRequestMoreAccounts, onRequestMoreUsers, onRequestMorePortal, onCustomPlan,
                                retention = null}) {
    const {t, i18n} = useTranslation();
    const lang = i18n && i18n.language;
    const [{period, currency}, setChoice] = useState(() => defaultChooserState(price, subscriptionCurrency));
    const currentId = isCustom ? null : baseTier(tier);
    const next = nextPlanOf(tier);
    const roomBadge = !!next && showsRoomBadge(usage);
    const portal = usage && usage.portal_accesses;

    return (
        <div className="sp-chooser">
            <LinkButton className="sp-back" onClick={onBack}>
                <ArrowLeft size={14} aria-hidden="true"/>{t(`${NS}.plans.back`)}
            </LinkButton>
            <h1 className="sp-title">{t(`${NS}.plans.title`)}</h1>
            <p className="sp-lead">{t(`${NS}.plans.lead`)}</p>
            {isCustom && <p className="sp-chooser__custom">{t(`${NS}.plan.custom`)}</p>}

            <TodayStrip usage={usage}/>

            <div className="sp-controls">
                <div className="sp-seg" role="group" aria-label={t(`${NS}.payments.col_period`)}>
                    {PERIOD_IDS.map((id) => (
                        <button key={id} type="button" aria-pressed={period === id} className={period === id ? '-on' : ''}
                                onClick={() => setChoice({period: id, currency})}>
                            {t(`agency_subscription.period_${id}`)}
                            {periodDiscount(id) > 0 && <span className="sp-seg__save">−{periodDiscount(id)}%</span>}
                        </button>
                    ))}
                </div>
                <div className="sp-seg" role="group" aria-label={t(`${NS}.billing.currency`)}>
                    {CURRENCY_IDS.map((id) => (
                        <button key={id} type="button" aria-pressed={currency === id} className={currency === id ? '-on' : ''}
                                onClick={() => setChoice({period, currency: id})}>
                            {id}
                        </button>
                    ))}
                </div>
            </div>

            <div className="sp-plans">
                {PLANS.map((plan) => (
                    <PlanCard key={plan.id} plan={plan} isCurrent={plan.id === currentId} tier={tier} isCustom={isCustom}
                              price={price} currency={currency} period={period} usage={usage} lang={lang}
                              retention={retention} roomBadge={roomBadge && next.id === plan.id}
                              onPick={() => onPickPlan(plan, {period, currency})}/>
                ))}
            </div>
            <p className="sp-footnote">{t(`${NS}.plans.footnote_${currency.toLowerCase()}`)}</p>

            <section className="sp-card" aria-labelledby="sp-included-title">
                <h2 id="sp-included-title" className="sp-card__title">{t(`${NS}.plans.included_title`)}</h2>
                <ul className="sp-chips">
                    {t(`${NS}.plans.included_list`).split(' · ').map((item) => <li key={item}>{item}</li>)}
                </ul>
            </section>

            <section className="sp-addons" aria-labelledby="sp-addons-title">
                <h2 id="sp-addons-title" className="sp-card__title">{t(`${NS}.plans.addons_title`)}</h2>
                <p className="sp-muted">{t(`${NS}.plans.addons_lead`)}</p>
                <div className="sp-addons__grid">
                    <Addon title={t(`${NS}.plans.addon_accounts`)}
                           price={addonPriceText(t, 'accounts', {currency, lang})}
                           cta={t(`${NS}.plans.addon_accounts_cta`)} onClick={onRequestMoreAccounts}/>
                    <Addon title={t(`${NS}.plans.addon_user`)} price={addonPriceText(t, 'user', {currency, lang})}
                           cta={t(`${NS}.plans.addon_user_cta`)} onClick={() => onRequestMoreUsers(currency)}/>
                    <Addon title={t(`${NS}.plans.addon_portal`)} price={t(`${NS}.plans.addon_portal_price`)}
                           cta={t(`${NS}.plans.addon_portal_cta`)} onClick={() => onRequestMorePortal(portal)}/>
                </div>
            </section>

            <section className="sp-card sp-custom" aria-labelledby="sp-custom-title">
                <div>
                    <h2 id="sp-custom-title" className="sp-custom__title">{t(`${NS}.plans.custom_title`)}</h2>
                    <p className="sp-muted">{t(`${NS}.plans.custom_body`)}</p>
                </div>
                <div className="sp-actions">
                    <button type="button" className="btn -alt -negative" onClick={onCustomPlan}>
                        {t(`${NS}.plans.custom_cta`)}
                    </button>
                </div>
            </section>
        </div>
    );
}

/** «Hoy usas …», con lo que viene en `usage`; sin `usage` no se dibuja. */
function TodayStrip({usage}) {
    const {t} = useTranslation();
    if (!usage) return null;
    const items = [
        ['accounts', usage.ad_accounts],
        ['users', usage.team_users],
        ['portal', usage.portal_accesses],
    ].filter(([, entry]) => entry && typeof entry.used === 'number');
    if (items.length === 0) return null;
    return (
        <div className="sp-today" aria-label={t(`${NS}.plans.today`)}>
            <span className="sp-today__label">{t(`${NS}.plans.today`)}</span>
            {items.map(([key, entry]) => (
                <span key={key}>{t(`${NS}.plans.today_${key}`, {count: entry.used})}</span>
            ))}
        </div>
    );
}

function PlanCard({plan, isCurrent, tier, isCustom, price, currency, period, usage, lang, retention, roomBadge,
                      onPick}) {
    const {t} = useTranslation();
    const name = t(plan.nameKey);
    const quantities = isCurrent ? currentQuantitiesOf(plan, usage) : plan.quantities;
    const fit = planFit(plan, {isCurrent, usage});
    const direction = isCustom ? null : planDirection(tier, plan.id);
    // Con un descuento de retención, contra el precio pleno (UD-17): es lo que va a pagar después.
    const delta = isCurrent ? null : planDelta(plan.id, price, currency, period, retention);
    const list = listMonthly(plan.id, currency, period);

    let badge = null;
    if (isCurrent) badge = <span className="sp-plan-card__badge -current">{t(`${NS}.plans.your_plan`)}</span>;
    else if (roomBadge) badge = <span className="sp-plan-card__badge -room">{t(`${NS}.plans.badge_room`)}</span>;

    return (
        <article className={`sp-plan-card${isCurrent ? ' -current' : ''}${roomBadge ? ' -room' : ''}`}
                 aria-labelledby={`sp-plan-${plan.id}`} data-testid={`sp-plan-card-${plan.id}`}>
            {badge}
            <div>
                <h2 id={`sp-plan-${plan.id}`} className="sp-plan-card__name">{name}</h2>
                <p className="sp-plan-card__desc">{t(plan.descriptionKey)}</p>
            </div>
            <PlanPrice isCurrent={isCurrent} price={price} list={list} currency={currency} delta={delta} lang={lang}
                       retention={retention}/>
            <ul className="sp-plan-card__list">
                <li>
                    <Check size={14} weight="bold" aria-hidden="true"/>
                    <span>
                        {t('subscription_features.connected_accounts', {accounts: quantities.linkedAccounts})}
                        <span className="sp-plan-card__hint">
                            {t('subscription_features.approx_clients', {clients: quantities.approxClients})}
                        </span>
                    </span>
                </li>
                <li>
                    <Check size={14} weight="bold" aria-hidden="true"/>
                    <span>{quantities.teamUsers === null
                        ? t(`${NS}.plans.unlimited_users`)
                        : t('subscription_features.team_users', {users: quantities.teamUsers})}</span>
                </li>
                <li>
                    <Check size={14} weight="bold" aria-hidden="true"/>
                    <span>{t('subscription_features.portal_accesses', {accesses: quantities.portalAccesses})}</span>
                </li>
                {plan.premiumSupport && (
                    <li><Check size={14} weight="bold" aria-hidden="true"/><span>{t(`${NS}.plans.premium`)}</span></li>
                )}
            </ul>
            {fit && <PlanFitLine fit={fit}/>}
            <div className="sp-plan-card__cta">
                <PlanCta isCurrent={isCurrent} direction={direction} name={name} roomBadge={roomBadge} onPick={onPick}/>
            </div>
        </article>
    );
}

/**
 * En el actual, lo que paga ESTA agencia (o nada, sin `price`); en los otros, la lista y la diferencia.
 * Durante un descuento de retención (UD-17 M1), «Tu precio actual» dice hasta cuándo y a cuánto vuelve. Antes
 * de que empiece no: hoy paga el precio pleno, y la línea diría un precio que todavía no rige.
 */
function PlanPrice({isCurrent, price, list, currency, delta, lang, retention}) {
    const {t} = useTranslation();
    if (isCurrent) {
        if (!price) return null;
        return (
            <div className="sp-plan-card__price">
                <div className="sp-plan-card__amount">
                    {formatAmount(price.monthlyEquivalent, price.currency, lang)}
                    {' '}<small>{t(`${NS}.plan.per_month`)}</small>
                </div>
                <p className="sp-plan-card__sub">
                    {retention && retention.state === 'active'
                        ? t(`${NS}.retention.your_price`, {
                            amount: retentionAmountText(t, retention.amount, retention.currency, price.plusVat, lang),
                            end: formatLongDate(retention.endsOn, lang),
                            full: retentionAmountText(t, retention.fullAmount, retention.currency, price.plusVat,
                                lang),
                            interpolation: RAW,
                        })
                        : t(`${NS}.plans.your_price`)}
                </p>
            </div>
        );
    }
    if (list === null) return null;
    return (
        <div className="sp-plan-card__price">
            <div className="sp-plan-card__amount">
                {formatAmount(list, currency, lang)}{' '}<small>{t(`${NS}.plan.per_month`)}</small>
            </div>
            {delta && (
                <p className="sp-plan-card__delta">
                    {t(`${NS}.plans.${delta.more ? 'more_than_today' : 'less_than_today'}`, {
                        amount: formatAmount(delta.amount, delta.currency, lang), interpolation: RAW,
                    })}
                </p>
            )}
        </div>
    );
}

function PlanFitLine({fit}) {
    const {t} = useTranslation();
    if (fit.kind === 'over') {
        return (
            <p className="sp-plan-card__fit -over">
                {t(`${NS}.plans.fit_over`, {used: fit.used, limit: fit.limit, interpolation: RAW})}
            </p>
        );
    }
    const key = fit.kind === 'left' ? 'fit_left' : 'fit_room';
    return <p className="sp-plan-card__fit">{t(`${NS}.plans.${key}`, {count: fit.count})}</p>;
}

function PlanCta({isCurrent, direction, name, roomBadge, onPick}) {
    const {t} = useTranslation();
    if (isCurrent) return <span className="sp-plan-card__current">{t(`${NS}.plans.is_current`)}</span>;
    let label = t(`${NS}.confirm.send`);
    if (direction === 'up') label = t(`${NS}.plans.cta_upgrade`, {plan: name, interpolation: RAW});
    if (direction === 'down') label = t(`${NS}.plans.cta_downgrade`, {plan: name, interpolation: RAW});
    return (
        <button type="button" className={`btn -alt${roomBadge ? '' : ' -negative'}`} onClick={onPick}>
            {label}
        </button>
    );
}

function Addon({title, price, cta, onClick}) {
    return (
        <div className="sp-addon">
            <div>
                <p className="sp-option__title">{title}</p>
                <p className="sp-option__body">{price}</p>
            </div>
            <button type="button" className="btn -alt -negative" onClick={onClick}>{cta}</button>
        </div>
    );
}
